import { Component, EventEmitter, Input, OnInit, Optional, Output } from '@angular/core'
import { environment } from '../../environments/environment'
import { BookingModalPresentationMode, VoucherLaunchContext } from '../landing-page/modal/landing-page-launch.types'
import { Venue } from '../domain/Venue'
import { VenueService } from '../services/venue.service'
import { mergeMap, Observable } from 'rxjs'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { Voucher } from '../domain/Voucher'
import { VoucherService } from '../services/voucher.service'
import { Helper } from '../utilities/helper'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { Sale } from '../domain/Sale'

enum VoucherModalStep {
    SKUSelection = 'SKUSelection',
    Checkout = 'Checkout',
    PaymentConfirmation = 'PaymentConfirmation',
}

@Component({
    selector: 'app-voucher-modal',
    templateUrl: './voucher-modal.component.html',
    styleUrls: ['./voucher-modal.component.sass'],
})
export class VoucherModalComponent implements OnInit {

    @Input() organisationId!: string
    @Input() businessId!: string
    @Input() venueId!: string
    @Input() voucherId!: string
    @Input() presentationMode = BookingModalPresentationMode.LegacyIframe
    @Output() dismissed = new EventEmitter<void>()
    form!: FormGroup
    isLoading = false
    marketingUrl = environment.marketingUrl
    venue$!: Observable<Venue>
    voucher$!: Observable<Voucher>
    confirmedSale: Sale | null = null

    constructor(
        private fb: FormBuilder,
        private venueService: VenueService,
        private voucherService: VoucherService,
        @Optional() private activeModal: NgbActiveModal | null
    ) { }

    ngOnInit() {
        this.form = this.makeForm()
        this.bindVoucherStreamsIfReady()
    }

    configureForNgbModal(context: VoucherLaunchContext) {
        this.presentationMode = BookingModalPresentationMode.NgbModal
        this.organisationId = context.organisationId
        this.businessId = context.businessId
        this.venueId = context.venueId
        this.voucherId = context.voucherId
        this.bindVoucherStreamsIfReady()
    }

    dismiss() {
        this.dismissed.emit()
        if (this.presentationMode === BookingModalPresentationMode.NgbModal) {
            this.activeModal?.dismiss()
            return
        }
        window.parent.postMessage(
            {
                type: 'close-booking-modal',
                payload: {
                },
            },
            '*'
        )
    }

    goToSKUSelection() {
        this.form.get('step')?.setValue(VoucherModalStep.SKUSelection)
    }

    goToCheckout(venue?: Venue) {
        if (venue && !this.canPurchaseVoucher(venue)) {
            return
        }
        this.form.markAllAsTouched()
        this.form.updateValueAndValidity()
        if (this.form.invalid) {
            return
        }
        this.form.get('step')?.setValue(VoucherModalStep.Checkout)
    }

    paymentConfirmed(sale: Sale) {
        this.confirmedSale = sale
        this.form.get('step')?.setValue(VoucherModalStep.PaymentConfirmation)
    }

    private makeForm() {
        return this.fb.group({
            skuId: [null, [Validators.required]],
            quantity: [1, [Validators.required, Validators.min(1), Validators.max(20), Validators.pattern(/^[1-9]\d*$/)]],
            step: [VoucherModalStep.SKUSelection, [Validators.required]],
        })
    }

    private bindVoucherStreamsIfReady() {
        if (!this.venueId || !this.voucherId) {
            return
        }
        this.venue$ = this.venueService.getWithId(this.venueId)
        this.voucher$ = this.venue$.pipe(
            mergeMap(venue => {
                return this.voucherService.getWithId(venue, this.voucherId)
            })
        )
    }

    private canPurchaseVoucher(venue: Venue): boolean {
        return Boolean(venue.paymentsAccountForCapability('VoucherPayments'))
    }

    get suppressIframeModalChrome(): boolean {
        return this.presentationMode === BookingModalPresentationMode.NgbModal
    }

    protected readonly Helper = Helper
    protected readonly VoucherModalStep = VoucherModalStep
}
